// ============================================================================
// BOUTON « COLONNES » + POPOVER — visibilité, ordre, et les DEUX cadenas (`locked` = non masquable,
// `pinned` = place imposée), dont les recettes vivent dans `ui-tokens` et non ici.
//
// ⚠️ FICHIER SÉPARÉ DU TABLEAU pour deux raisons : c'est un patron réutilisable hors tableau, et il
// porte à lui seul la mécanique d'ancrage MESURÉ (§13.33). Le tableau, lui, n'a qu'à savoir qu'un
// `ColumnLayout` peut changer.
//
// ⚠️ IL N'ÉCRIT NULLE PART : ni localStorage, ni fetch, ni contexte. La persistance exigée est « par
// utilisateur × par table, EN BASE » — un composant partagé ne connaît ni le client HTTP, ni la
// session, ni le magasin de l'app. S'il écrivait en localStorage « en attendant », le réglage ne
// suivrait pas l'utilisateur d'un poste à l'autre : exactement ce que la règle interdit, et le repli
// deviendrait définitif. Il fournit la MÉCANIQUE (`reconcileColumnLayout`, `tablePrefsKey`), pas le
// stockage.
//
// ⚠️ SES RECETTES CSS VIVENT SOUS `.dt-col-panel`, qu'il est SEUL à poser — et non sous la racine du
// tableau : il doit rester employable hors tableau.
// ============================================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import {
  TABLE_COLUMNS,
  isColumnHideable,
  isColumnMovable,
  sortByPinnedRank,
  type ColumnLayout,
  type ColumnLockState,
  type ColumnPinState,
  anchorPanel as anchorPanelPartage,
} from '@orgazone/ui-tokens';
import { Icon } from './icons';
import { useTileDrag } from './tile-drag';
import { useSortiesDEnvironnement } from './popover';
import { t } from '@orgazone/i18n';

/**
 * Une colonne, vue par le popover : il ignore tout du rendu des cellules.
 * ⚠️ Elle COMPOSE les deux états partagés (`ColumnLockState`, `ColumnPinState`) au lieu de redéclarer
 * `locked`/`hideable`/`pinned` : ce sont les mêmes propriétés que celles du tableau, lues par les
 * mêmes recettes. Redéclarées, elles finiraient par ne plus décrire tout à fait la même chose.
 */
export interface ColumnsButtonColumn extends ColumnLockState, ColumnPinState {
  key: string;
  label: string;
}

export interface ColumnsButtonProps {
  columns: ColumnsButtonColumn[];
  layout: ColumnLayout;
  onChange: (next: ColumnLayout, reason: 'toggle' | 'move' | 'reset') => void;
  /**
   * Réordonnancement possible ? Défaut `false`.
   * ⚠️ CE QUI EST BRANCHÉ : les flèches ↑/↓ au clavier et deux boutons Monter/Descendre. Le GLISSER au
   * pointeur (patron §4 : fantôme cloné, placeholder, voisines en FLIP) n'est pas implémenté — aucune
   * poignée n'est donc rendue, et aucune recette de glisser n'est livrée. Dessiner une prise qui ne
   * saisit rien serait une option offerte non implémentée (§23.0) ; l'ordre, lui, se change réellement.
   * ⚠️ Même posé, il n'offre RIEN sur une colonne `pinned` : sa place est imposée par le tableau.
   */
  reorderable?: boolean;
  /**
   * Phrase de portée du pied (« mémorisés sur votre profil, tous vos postes »).
   * ⚠️ Rendue SEULEMENT si l'app la fournit : la maquette l'affiche alors que rien n'est branché, et
   * l'utilisateur repose ses colonnes à chaque visite en croyant à un bug plutôt qu'à une absence.
   */
  persistenceNote?: string;
  /**
   * Libellé du pied. Défaut « Réinitialiser » (charte) : le popover gère désormais visibilité ET
   * ordre, et « Tout afficher » (maquette) ne décrit que la moitié de ce que le bouton fait.
   * ⚠️ Reste une PROP parce que l'arbitrage n'est pas signé — figer le mot dans le code fermerait la
   * discussion au lieu de la porter.
   */
  resetLabel?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// LOGIQUE PURE — testée sans DOM. C'est elle qui tient la promesse « souris et clavier produisent
// exactement le même résultat » : les deux gestes passent par `moveColumnKeyAmong`, il n'y a pas deux
// recettes de déplacement à faire diverger.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Ce que la ligne du popover offre à droite : les flèches, la mention d'épinglage, ou rien.
 *
 * ⚠️ UNE COLONNE ÉPINGLÉE NE SE VOIT PLUS OFFRIR Monter/Descendre (arbitrage utilisateur 2026-08-16).
 * Les offrir éteintes ne suffisait pas : un contrôle `disabled` ne reçoit aucun événement de survol,
 * donc l'infobulle qui portait la raison ne s'affichait jamais — un bouton gris muet, la version douce
 * du mensonge. La mention prend leur place et porte la raison, en texte et en infobulle.
 */
export function columnRowControl(col: ColumnsButtonColumn, reorderable: boolean): 'move' | 'pinned' | 'none' {
  if (!reorderable) return 'none';
  return isColumnMovable(col) ? 'move' : 'pinned';
}

/** Déplace une clé de `delta` positions dans l'ordre. Hors bornes : l'ordre est rendu inchangé. */
export function moveColumnKey(order: readonly string[], key: string, delta: number): string[] {
  const from = order.indexOf(key);
  if (from < 0) return [...order];
  const to = from + delta;
  if (to < 0 || to >= order.length) return [...order];
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, key);
  return next;
}

/**
 * Déplace une clé d'un cran PARMI LES DÉPLAÇABLES : les épinglées sont sautées, jamais bousculées.
 * ⚠️ Sans ce saut, échanger une colonne avec sa voisine épinglée modifiait le réglage mémorisé sans
 * rien changer à l'écran (le tableau ramène les épinglées à leur bord) — un bouton qui fait semblant,
 * doublé d'une écriture serveur inutile.
 */
export function moveColumnKeyAmong(
  order: readonly string[],
  key: string,
  delta: -1 | 1,
  movable: (key: string) => boolean,
): string[] {
  const from = order.indexOf(key);
  if (from < 0 || !movable(key)) return [...order];
  let to = from + delta;
  while (to >= 0 && to < order.length && !movable(order[to] as string)) to += delta;
  if (to < 0 || to >= order.length) return [...order];
  return moveColumnKey(order, key, to - from);
}

/**
 * L'ORDRE FINAL APRÈS UN GLISSER — les épinglées reprennent leur place, les autres suivent le geste.
 *
 * ⚠️ ELLE EXISTE POUR QUE LE POINTEUR NE PUISSE PAS FAIRE CE QUE LE CLAVIER REFUSE. Les flèches
 * sautent les épinglées (`moveColumnKeyAmong`) ; un glisser libre, lui, pourrait déposer une colonne
 * entre deux épinglées ou les décaler — le réglage serait mémorisé, et le tableau les ramènerait à
 * leur bord au rendu suivant. Le geste aurait « réussi » à l'écran et échoué dans les faits.
 *
 * ⚠️ LA RÈGLE EST SIMPLE ET VÉRIFIABLE : on reparcourt l'ordre d'origine ; à chaque position occupée
 * par une épinglée, elle reste ; partout ailleurs, on prend la déplaçable suivante telle que le
 * glisser les a classées.
 */
export function mergePinnedOrder(
  origine: readonly string[],
  glisse: readonly string[],
  movable: (key: string) => boolean,
): string[] {
  const suite = glisse.filter((k) => origine.includes(k) && movable(k));
  // Un glisser qui aurait perdu ou inventé une clé ne doit rien réécrire du tout.
  if (suite.length !== origine.filter(movable).length) return [...origine];
  let i = 0;
  return origine.map((k) => (movable(k) ? (suite[i++] as string) : k));
}

/** Le déplacement est-il possible ? C'est ce qui éteint RÉELLEMENT les boutons ▲▼, aux deux bouts. */
export function canMoveColumn(
  order: readonly string[],
  key: string,
  delta: -1 | 1,
  movable: (key: string) => boolean,
): boolean {
  const from = order.indexOf(key);
  if (from < 0 || !movable(key)) return false;
  for (let i = from + delta; i >= 0 && i < order.length; i += delta) {
    if (movable(order[i] as string)) return true;
  }
  return false;
}

/**
 * Bascule la visibilité d'une colonne.
 * ⚠️ Une colonne non masquable est rendue INCHANGÉE : offrir la bascule puis l'ignorer serait une
 * option offerte non implémentée. Le popover ne rend d'ailleurs aucune case pour elle.
 */
export function toggleColumnHidden(layout: ColumnLayout, col: ColumnsButtonColumn): ColumnLayout {
  if (!isColumnHideable(col)) return layout;
  const hidden = new Set(layout.hidden);
  if (!hidden.delete(col.key)) hidden.add(col.key);
  return { order: [...layout.order], hidden: [...hidden] };
}

/** Réglage par défaut : ordre DÉCLARÉ et rien de masqué — visibilité ET ordre, comme le dit « Réinitialiser ». */
export function resetColumnLayout(columns: readonly ColumnsButtonColumn[]): ColumnLayout {
  return { order: columns.map((c) => c.key), hidden: [] };
}

/** Combien de colonnes sont réellement masquées (les non-masquables ne comptent jamais). */
export function hiddenColumnCount(columns: readonly ColumnsButtonColumn[], layout: ColumnLayout): number {
  const hidden = new Set(layout.hidden);
  return columns.filter((c) => isColumnHideable(c) && hidden.has(c.key)).length;
}

/** Libellé du bouton. Le compteur n'apparaît QUE s'il y a quelque chose à compter. */
export function columnsButtonLabel(count: number): string {
  if (count <= 0) return t('Colonnes');
  return t('Colonnes · {n} masquée', { n: count });
}

/**
 * Les lignes du panneau, DANS L'ORDRE OÙ LE TABLEAU LES REND (épinglées à leur bord).
 * ⚠️ Le panneau liste TOUT, masquées comprises — c'est son objet — mais il ne peut pas annoncer un
 * ordre que l'écran ne montre pas. Le classement vient donc de `sortByPinnedRank` (`ui-tokens`), la
 * MÊME fonction que celle appelée par `visibleColumns` : c'est un appel partagé, pas deux formules
 * jumelles — la version précédente avait fini avec une recette de chaque côté.
 */
export function panelColumns(columns: readonly ColumnsButtonColumn[], layout: ColumnLayout): ColumnsButtonColumn[] {
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const ordered = layout.order
    .map((k) => byKey.get(k))
    .filter((c): c is ColumnsButtonColumn => Boolean(c));
  return sortByPinnedRank(ordered, (c) => c.pinned);
}

/**
 * Position du panneau, BORNÉE à la fenêtre.
 * ⚠️ Ancrage MESURÉ et `position:fixed`, pas `absolute` : un menu déroulant dans un conteneur défilant
 * est rogné par l'ancêtre en overflow (mesuré : panneau de 317 px dans un corps de 300 px, bouton
 * « Créer » jamais atteignable), et en flux il décalerait la page. Or le tableau vit précisément dans
 * un conteneur défilant borné, par construction.
 * ⚠️ BASCULE AU-DESSUS quand la place manque dessous. `minHeight` était appliqué en PLANCHER
 * (`Math.max`), ce qui annulait le plafonnement : un déclencheur en bas d'écran produisait un panneau
 * débordant de la fenêtre, pied compris — donc « Réinitialiser » inatteignable, le défaut même que le
 * plafond devait empêcher. Le panneau ne dépasse plus jamais : il change de côté, ou il rétrécit.
 */
export function anchorPanel(
  trigger: { left: number; right: number; top: number; bottom: number },
  viewport: { width: number; height: number },
): { left: number; top: number; maxHeight: number } {
  // ⚠️ UNE SEULE IMPLÉMENTATION (extraction P2, 2026-08-16) : le calcul vit désormais dans
  // `@orgazone/ui-tokens/popover`, et le panneau de colonnes en devient un PRESET. Cette fonction
  // reste exportée telle quelle — ses tests de non-régression (bascule au-dessus, bornage, bord
  // haut) prouvent l'équivalence, et c'est exactement ce qu'on veut d'une extraction.
  const p = TABLE_COLUMNS.panel;
  return anchorPanelPartage(trigger, viewport, {
    width: p.width, offsetY: p.offsetY, edgeGap: p.edgeGap, minHeight: p.minHeight,
    maxHeight: p.maxHeight, viewportPad: p.viewportPad, align: 'end',
  });
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// RENDU
// ═══════════════════════════════════════════════════════════════════════════════════════════════

export function ColumnsButton({ columns, layout, onChange, reorderable = false, persistenceNote, resetLabel = t('Réinitialiser') }: ColumnsButtonProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const focusedOnOpen = useRef(false);

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(anchorPanel({ left: r.left, right: r.right, top: r.top, bottom: r.bottom }, { width: window.innerWidth, height: window.innerHeight }));
  }, []);

  useLayoutEffect(() => { if (open) measure(); }, [open, measure]);

  /**
   * LE chemin de fermeture — il n'y en a qu'un, et il REND LE FOCUS au déclencheur.
   * ⚠️ Les quatre sorties (Échap, voile, défilement d'un ancêtre, redimensionnement) passent toutes
   * ici. Deux d'entre elles appelaient `setOpen(false)` nu : le panneau, qui prend le focus à
   * l'ouverture, était démonté en le portant — le focus retombait sur `<body>` et l'utilisateur au
   * clavier perdait sa place dans la barre d'outils.
   * ⚠️ `preventScroll` : sur une fermeture PROVOQUÉE par un défilement, rendre le focus ferait
   * remonter la page au déclencheur — l'écran se battrait avec le geste de l'utilisateur.
   */
  const closeAndRestore = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  /** Au doigt, la réponse au défilement : suivre son déclencheur — et se fermer s'il quitte l'écran. */
  const suivre = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) { closeAndRestore(); return; }
    measure();
  }, [closeAndRestore, measure]);

  // ⚠️ Le panneau prend le focus à l'ouverture : sans lui, Échap n'a nulle part où être entendu et
  // l'utilisateur au clavier doit retraverser toute la liste pour revenir au déclencheur.
  useEffect(() => {
    if (!open) { focusedOnOpen.current = false; return; }
    if (focusedOnOpen.current || !panelRef.current) return;
    focusedOnOpen.current = true;
    panelRef.current.focus({ preventScroll: true });
  }, [open, pos]);

  // ⚠️ Défilement et redimensionnement passent par la règle PARTAGÉE (`useSortiesDEnvironnement`) : ce
  // panneau portait sa propre copie, et le correctif tactile n'y avait pas été reporté — il gardait
  // exactement le défaut qu'on venait de corriger dans le picker (« je ne peux pas descendre dans le
  // select des colonnes non plus »).
  // ⚠️ Échap ferme et rend le focus : toute la charte pose Échap comme sortie (picker §1, modale §6,
  // tiroir §10) ; un `role="dialog"` sans cette sortie n'en est pas une.
  useSortiesDEnvironnement(open, panelRef, closeAndRestore, suivre);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); closeAndRestore(); } };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeAndRestore]);

  const count = hiddenColumnCount(columns, layout);
  const ordered = panelColumns(columns, layout);
  const hidden = new Set(layout.hidden);
  const movableKeys = new Set(columns.filter(isColumnMovable).map((c) => c.key));
  const isMovable = (key: string) => movableKeys.has(key);

  // ⚠️ LE GLISSER PASSE PAR LA MÊME RÈGLE QUE LES FLÈCHES. Le geste rend un ordre libre ; c'est
  //    `mergePinnedOrder` qui y remet les épinglées à leur place, exactement comme
  //    `moveColumnKeyAmong` les saute au clavier. Sans ce passage obligé, le pointeur pourrait
  //    déposer une colonne entre deux épinglées : le réglage serait mémorisé, et le tableau les
  //    ramènerait à leur bord au rendu suivant — un geste qui réussit à l'écran et échoue en base.
  const drag = useTileDrag({
    keys: ordered.map((c) => c.key),
    movable: isMovable,
    axis: 'y',
    disabled: !reorderable,
    // ⚠️ Le fantôme est porté dans «body» : sans cette classe il sort de «.dt-col-panel .dt-col-row»
    //    et s'affiche VIDE — constaté à l'écran le 2026-08-19.
    ghostScope: 'dt-col-panel',
    onReorder: (next) => onChange(
      { order: mergePinnedOrder(layout.order, next, isMovable), hidden: [...layout.hidden] },
      'move',
    ),
  });
  // L'ordre RENDU suit l'aperçu du glisser : c'est ce qui fait s'écarter les voisines pendant le geste.
  const parCle = new Map(ordered.map((c) => [c.key, c]));
  const rendu = drag.order.map((k) => parCle.get(k)).filter(Boolean) as ColumnsButtonColumn[];

  // ⚠️ LA MÊME GARDE POUR LE CLAVIER ET POUR LA SOURIS : les boutons ▲▼ sont réellement `disabled`
  // par `canMoveColumn`, mais le raccourci clavier appelait `move()` sans la consulter — deux chemins
  // qui décidaient différemment de la même chose, alors que le commentaire voisin affirmait le
  // contraire.
  const move = (key: string, delta: -1 | 1) => {
    if (!canMoveColumn(layout.order, key, delta, isMovable)) return;
    const ordre = moveColumnKeyAmong(layout.order, key, delta, isMovable);
    onChange({ order: ordre, hidden: [...layout.hidden] }, 'move');
    // ⚠️ LE FOCUS NE TOMBE PAS SUR <body>. Amener une colonne en tête DÉSACTIVE le bouton dont on
    // vient de se servir ; le navigateur retire alors le focus de l'élément désactivé et le repose
    // sur le document — au clavier, on sort du panneau sans l'avoir demandé, et Échap n'est plus
    // entendu. On le porte sur la flèche OPPOSÉE, qui reste active par construction (une colonne qui
    // ne peut plus monter peut descendre), et à défaut sur le panneau lui-même.
    queueMicrotask(() => {
      const oppose = panelRef.current?.querySelector<HTMLButtonElement>(
        `[data-move="${delta === -1 ? 'down' : 'up'}"][data-col="${CSS.escape(key)}"]`,
      );
      if (oppose && !oppose.disabled) oppose.focus();
      else panelRef.current?.focus();
    });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // La peau de marque n'apparaît qu'avec le compteur : sans colonne masquée, le bouton n'annonce rien.
        // ⚠️ Format `sm` ARBITRÉ (2026-08-16) et non le « ghost md » de la charte §3b : ce bouton vit dans
        // la barre d'outils du tableau, aux côtés de l'export, où 36 px pousseraient la barre. Écart
        // consigné plutôt que laissé tacite.
        className={`btn-ghost sm${count > 0 ? ' marque' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="layers" size={14} style={{ marginRight: 6 }} />
        {columnsButtonLabel(count)}
      </button>

      {open && pos && (
        <>
          <div className="dt-col-scrim" onClick={closeAndRestore} />
          {/* Le fantôme du glisser — il se porte tout seul (portail, position fixe). */}
          {drag.ghost}
          <div
            ref={panelRef}
            className="dt-col-panel oz-pop"
            role="dialog"
            aria-label={t('Colonnes affichées')}
            tabIndex={-1}
            style={{ left: pos.left, top: pos.top, maxHeight: pos.maxHeight } as CSSProperties}
          >
            <div className="dt-col-head">{t('Colonnes affichées')}</div>
            <div className="dt-col-list">
              {rendu.map((col) => {
                const hideable = isColumnHideable(col);
                const off = hidden.has(col.key);
                const control = columnRowControl(col, reorderable);
                // ⚠️ LA LIGNE SAISIE CÈDE ENTIÈREMENT SA PLACE au creux fabriqué par le module :
                //    la restyler laissait apparaître sa case à cocher et ses pastilles, qui portent
                //    leur propre fond et survivent à toute couleur posée sur le parent.
                if (drag.grabbed === col.key) return drag.placeholder;
                return (
                  <div
                    key={col.key}
                    className={`dt-col-row${hideable ? '' : ' locked'}`}
                    ref={drag.tile(col.key).ref}
                    data-tile-key={col.key}
                    // ⚠️ LES STYLES DE REPOS viennent du module (`user-select`/`touch-action` neutralisés
                    //    dès le repos) : sans eux, le premier mouvement sélectionne le texte de la ligne
                    //    au lieu de la saisir.
                    style={drag.tile(col.key).style}
                    // ⚠️ CONTENEUR NEUTRE, sans rôle ni tabIndex : un widget `checkbox` ne peut pas
                    // contenir de descendants focalisables (les boutons ▲▼ y étaient annoncés dans un
                    // contexte incohérent et leur libellé absorbé par le nom de la case).
                    onKeyDown={(e) => {
                      // ⚠️ Les flèches font EXACTEMENT ce que font les boutons — même condition, même
                      // recette : c'est le clavier qui est le chemin de référence, le pointeur n'en est
                      // qu'un accélérateur. Une colonne épinglée n'en offre aucun des deux.
                      if (control !== 'move') return;
                      if (e.key === 'ArrowUp') { e.preventDefault(); move(col.key, -1); return; }
                      if (e.key === 'ArrowDown') { e.preventDefault(); move(col.key, 1); }
                    }}
                  >
                    {/* ⚠️ LA POIGNÉE N'APPARAÎT QUE SI LE GLISSER EXISTE POUR CETTE LIGNE : dessiner
                        une prise sur une colonne épinglée serait une option offerte non implémentée.
                        ⚠️ `aria-hidden` : le clavier a déjà les flèches ↑/↓ sur la ligne, qui font la
                        même chose. L'annoncer deux fois n'ajoute aucun pouvoir, seulement du bruit. */}
                    {control === 'move' && (
                      <span
                        aria-hidden
                        title={t('Glisser pour réordonner')}
                        onPointerDown={drag.handle(col.key).onPointerDown}
                        style={{
                          ...drag.handle(col.key).style,
                          display: 'inline-flex', alignItems: 'center', flexShrink: 0,
                          color: 'var(--ink-400)',
                        }}
                      >
                        {/* ⚠️ L'ICÔNE DU REGISTRE PARTAGÉ, jamais un caractère brut : `⠿` est un signe
                            braille, dont le dessin dépend de la police installée — et un lecteur
                            d'écran mal configuré l'annonce. `drag` existait déjà dans la charte. */}
                        <Icon name="drag" size={TABLE_COLUMNS.item.lockSize} />
                      </span>
                    )}
                    {hideable ? (
                      <button
                        type="button"
                        role="checkbox"
                        className="dt-col-toggle"
                        aria-checked={!off}
                        onClick={() => onChange(toggleColumnHidden(layout, col), 'toggle')}
                      >
                        <span className="dt-box" aria-hidden>{off ? '' : '✓'}</span>
                        <span className="dt-col-name">{col.label}</span>
                      </button>
                    ) : (
                      <span className="dt-col-toggle" title={t('Colonne d’identité : toujours affichée')}>
                        <Icon name="lock" size={TABLE_COLUMNS.item.lockSize} />
                        <span className="dt-col-name">{col.label}</span>
                      </span>
                    )}
                    {control === 'move' && (
                      <>
                        <button
                          type="button"
                          className="btn-icon sm"
                          aria-label={t('Monter {name}', { name: col.label })}
                          data-move="up"
                          data-col={col.key}
                          disabled={!canMoveColumn(layout.order, col.key, -1, isMovable)}
                          onClick={() => move(col.key, -1)}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="btn-icon sm"
                          aria-label={t('Descendre {name}', { name: col.label })}
                          data-move="down"
                          data-col={col.key}
                          disabled={!canMoveColumn(layout.order, col.key, 1, isMovable)}
                          onClick={() => move(col.key, 1)}
                        >
                          ▼
                        </button>
                      </>
                    )}
                    {/* ⚠️ MENTION, PAS DEUX BOUTONS ÉTEINTS : une colonne épinglée ne se voit plus
                        offrir Monter/Descendre du tout. Éteints, ils ne recevaient aucun survol —
                        donc leur infobulle, seule porteuse de la raison, ne s'affichait jamais. Ici
                        la raison est lisible sans geste, et l'infobulle la complète. */}
                    {control === 'pinned' && (
                      <span className="dt-col-pin" title={t('Colonne épinglée : sa place est imposée par le tableau')}>
                        
                        {t('Épinglée')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="dt-col-foot">
              {/* La phrase de portée n'existe que si l'app déclare vraiment mémoriser. */}
              <span>{persistenceNote ?? ''}</span>
              <button type="button" className="btn-link" onClick={() => onChange(resetColumnLayout(columns), 'reset')}>{resetLabel}</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
